import type { ReactNode } from 'react';

import { PROCESSED_NOTIFICATION_KINDS, type ProcessedNotificationKind } from 'cpool-constants';
import { LuBell, LuFileText, LuLandmark, LuStar, LuUsers } from 'react-icons/lu';

type NotificationCategoryMeta = {
    icon: ReactNode;
    iconClassName: string;
    iconContainerClassName: string;
    titleKey: string;
};

/**
 * Visual definition shared by every notification category. Both the unprocessed and processed tabs
 * render a single header per category from this map, so the icon, colours and title key live in one
 * place. Keyed by {@link ProcessedNotificationKind} because those are the notification categories.
 */
export const NOTIFICATION_CATEGORIES = {
    [PROCESSED_NOTIFICATION_KINDS.RESUBMISSION]: {
        icon: <LuBell />,
        iconClassName: 'text-red-500',
        iconContainerClassName: 'bg-red-500/10',
        titleKey: 'TOP_HEADER.NOTIFICATIONS.RESUBMISSIONS',
    },
    [PROCESSED_NOTIFICATION_KINDS.DOCUMENT]: {
        icon: <LuFileText />,
        iconClassName: 'text-orange-500',
        iconContainerClassName: 'bg-orange-500/10',
        titleKey: 'TOP_HEADER.NOTIFICATIONS.DOCUMENTS',
    },
    [PROCESSED_NOTIFICATION_KINDS.BANK_REPORT]: {
        icon: <LuLandmark />,
        iconClassName: 'text-blue-text',
        iconContainerClassName: 'bg-blue-cloud',
        titleKey: 'TOP_HEADER.NOTIFICATIONS.BANKREPORTS',
    },
    [PROCESSED_NOTIFICATION_KINDS.ASSIGNED_USER_ADDED]: {
        icon: <LuUsers />,
        iconClassName: 'text-green-600',
        iconContainerClassName: 'bg-green-600/10',
        titleKey: 'TOP_HEADER.NOTIFICATIONS.ADVISOR_SERVICE',
    },
    [PROCESSED_NOTIFICATION_KINDS.ACTIVE_USER_SET]: {
        icon: <LuStar />,
        iconClassName: 'text-midnight',
        iconContainerClassName: 'bg-midnight/10',
        titleKey: 'TOP_HEADER.NOTIFICATIONS.RESPONSIBILITY',
    },
    // `as const` keeps `titleKey` as a literal i18n key (so `t()` accepts it) while `satisfies`
    // still verifies every kind has a complete, well-typed entry.
} as const satisfies Record<ProcessedNotificationKind, NotificationCategoryMeta>;

/** Fixed display order of notification groups. */
export const NOTIFICATION_CATEGORY_ORDER: ProcessedNotificationKind[] = [
    PROCESSED_NOTIFICATION_KINDS.RESUBMISSION,
    PROCESSED_NOTIFICATION_KINDS.DOCUMENT,
    PROCESSED_NOTIFICATION_KINDS.BANK_REPORT,
    PROCESSED_NOTIFICATION_KINDS.ASSIGNED_USER_ADDED,
    PROCESSED_NOTIFICATION_KINDS.ACTIVE_USER_SET,
];
